import { type TilePos } from '@veriverse/game-logic';
import {
  findObjectLayer,
  findTileLayer,
  objectFootprint,
  objectTile,
  objectType,
  tileId,
} from './model.js';
import { readProperties } from './properties.js';
import { DEFAULT_LAMP_RADIUS, FOG_LAYER, OUTDOOR_LAYER, WINDOWS_LAYER } from './rules.js';
import { type TiledMap } from './types.js';

/** A window in tile units: the rectangle that shows the sky (spec §6.10). */
export interface MapWindow {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** A light source that glows after sunset. */
export interface Lamp {
  readonly id: number;
  /** Tile the lamp stands on; the glow is centred on it. */
  readonly tile: TilePos;
  /** Glow radius in tiles. */
  readonly radius: number;
}

/** Window rectangles from the optional `windows` layer (hidden objects are skipped). */
export function getWindows(map: TiledMap): MapWindow[] {
  return (findObjectLayer(map, WINDOWS_LAYER)?.objects ?? [])
    .filter((object) => object.visible && !object.point && object.width > 0 && object.height > 0)
    .map((object) => {
      const { x, y, w, h } = objectFootprint(object);
      return { id: object.id, x, y, width: w, height: h };
    });
}

/** Fog rectangles from the optional `fog` layer, in tile units (hidden objects are skipped). */
export function getFogAreas(map: TiledMap): MapWindow[] {
  return (findObjectLayer(map, FOG_LAYER)?.objects ?? [])
    .filter((object) => object.visible && !object.point && object.width > 0 && object.height > 0)
    .map((object) => {
      const { x, y, w, h } = objectFootprint(object);
      return { id: object.id, x, y, width: w, height: h };
    });
}

/** Objects of type `lamp` on the `objects` layer. */
export function getLamps(map: TiledMap): Lamp[] {
  return (findObjectLayer(map, 'objects')?.objects ?? [])
    .filter((object) => objectType(object) === 'lamp')
    .map((object) => {
      const radius = readProperties(object).radius;
      return {
        id: object.id,
        tile: objectTile(object),
        radius: typeof radius === 'number' && radius > 0 ? radius : DEFAULT_LAMP_RADIUS,
      };
    });
}

/**
 * Which tiles are outdoors, as a row-major array (1 = outdoor), from the optional `outdoor` tile
 * layer. Maps without the layer are entirely indoors.
 */
export function outdoorMask(map: TiledMap): Uint8Array {
  const mask = new Uint8Array(map.width * map.height);
  const layer = findTileLayer(map, OUTDOOR_LAYER);
  layer?.data.forEach((gid, index) => {
    if (index < mask.length && tileId(gid) !== 0) mask[index] = 1;
  });
  return mask;
}
